/**
 * Страница акции в кабинете: статистика, коды, розыгрыш, итоги.
 *
 * Розыгрыш здесь — отдельный экран с подтверждением, потому что это
 * необратимая публикация: после него победители опубликованы, а сид раскрыт.
 */
import type { Campaign, CampaignLink, ConsoleCampaignDetail } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import {
  Badge, Button, Card, Countdown, Empty, Field, Hash, KeyRow, Progress, Segmented,
  Sheet, Skeleton, fmtDateTime, fmtDay, num, percent, plural, ticketChance,
} from '@allswipes/ui';
import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../api';
import { Layout } from '../components/Layout';
import { MediaField } from '../components/MediaField';
import { useResource } from '../hooks';
import { canPublish, useConsole } from '../session';
import { useToast } from '../toast';
import { StatusBadge } from './Campaigns';

type Tab = 'overview' | 'codes' | 'draw' | 'data' | 'winners';

export function CampaignDetailScreen() {
  const { id = '' } = useParams();
  const detail = useResource(() => api.consoleCampaign(id), [id]);
  const [tab, setTab] = useState<Tab>('overview');

  if (detail.loading) {
    return (
      <Layout title="Акция">
        <Skeleton count={3} height={110} />
      </Layout>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <Layout title="Акция">
        <Empty icon="search" title="Акция не найдена">
          {detail.error}
        </Empty>
      </Layout>
    );
  }

  const d = detail.data;
  const hasDraw = Boolean(d.campaign.draw_at);

  const tabs: Array<{ value: Tab; label: string }> = [
    { value: 'overview', label: 'Обзор' },
    { value: 'codes', label: 'Коды' },
  ];
  if (hasDraw) tabs.push({ value: 'draw', label: 'Розыгрыш' });
  if ((d.campaign.config.fields?.length ?? 0) > 0) tabs.push({ value: 'data', label: 'Данные' });
  if ((d.winners ?? []).length > 0) tabs.push({ value: 'winners', label: 'Итоги' });

  return (
    <Layout
      title={d.campaign.name}
      subtitle={d.campaign.prize_title}
      action={
        <div className="row" style={{ gap: 8 }}>
          <StatusBadge status={d.campaign.status} />
          <Link className="btn out sm" to={`/campaigns/${d.campaign.id}/economics`}>
            Экономика
          </Link>
          <a className="btn out sm" href={d.landing_url} target="_blank" rel="noreferrer">
            Открыть страницу ↗
          </a>
        </div>
      }
    >
      <div style={{ marginBottom: 18, maxWidth: 520 }}>
        <Segmented value={tab} options={tabs} onChange={setTab} />
      </div>

      {tab === 'overview' && <Overview detail={d} onChanged={detail.reload} />}
      {tab === 'codes' && <Codes detail={d} onChanged={detail.reload} />}
      {tab === 'draw' && <Draw detail={d} onChanged={detail.reload} />}
      {tab === 'data' && <CollectedData campaignId={d.campaign.id} />}
      {tab === 'winners' && <Winners detail={d} />}
    </Layout>
  );
}

function Overview({ detail, onChanged }: { detail: ConsoleCampaignDetail; onChanged: () => void }) {
  const { campaign: c, stats, fair, distribution } = detail;
  const { me } = useConsole();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const close = async () => {
    setBusy(true);
    try {
      await api.closeCampaign(c.id);
      toast('Приём кодов закрыт, список билетов зафиксирован');
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  const odds = c.config.segments ?? c.config.scratch_prizes ?? [];
  const oddsTotal = odds.reduce((sum, o) => sum + Math.max(0, o.weight), 0);
  const outcomesTotal = Object.values(distribution ?? {}).reduce((a, b) => a + b, 0);

  return (
    <div className="stack">
      <div className="stats">
        <Stat value={num(stats.participants)} label="участников" />
        <Stat value={num(stats.entries)} label="кодов принято" />
        <Stat value={`${num(stats.codes_used)} / ${num(stats.codes_total)}`} label="кодов использовано" />
        <Stat value={num(stats.winners)} label="победителей" />
        <Stat value={num(stats.received)} label="призов получено" />
        <Stat
          value={stats.avg_rating > 0 ? stats.avg_rating.toFixed(1) : '—'}
          label="средняя оценка"
        />
      </div>

      <ProofCard campaign={c} onSaved={onChanged} />

      <Card>
        <div className="h2" style={{ marginBottom: 12 }}>
          Воронка
        </div>
        <Funnel
          rows={[
            ['Коды выпущены', stats.codes_total],
            ['Коды приняты', stats.codes_used],
            ['Участники', stats.participants],
            ['Победители', stats.winners],
            ['Оформили приз', stats.claimed],
            ['Получили', stats.received],
            ['Оставили отзыв', stats.reviewed],
          ]}
        />
      </Card>

      <div className="grid c2">
        <Card>
          <div className="h2" style={{ marginBottom: 10 }}>
            Условия
          </div>
          <KeyRow label="Механика">{c.recipe}</KeyRow>
          <KeyRow label="Регионы">{c.regions.join(' · ')}</KeyRow>
          <KeyRow label="Сроки">
            {fmtDay(c.start_at)} — {fmtDay(c.end_at)}
          </KeyRow>
          {c.draw_at && <KeyRow label="Розыгрыш">{fmtDateTime(c.draw_at)}</KeyRow>}
          <KeyRow label="Баллов за код">{num(c.pts_per_code)}</KeyRow>
          <KeyRow label="Срок получения">{c.handout_days} дней</KeyRow>
          {c.locked_at && <KeyRow label="Условия зафиксированы">{fmtDateTime(c.locked_at)}</KeyRow>}
          {c.rules_hash && (
            <KeyRow label="Хеш правил" mono>
              <Hash value={c.rules_hash} />
            </KeyRow>
          )}

          {c.status === 'active' && canPublish(me?.member.role) && (
            <div style={{ marginTop: 14 }}>
              <Button variant="out" block disabled={busy} onClick={() => void close()}>
                Закрыть приём кодов досрочно
              </Button>
              <p className="sub" style={{ marginTop: 8, fontSize: 12 }}>
                Список билетов будет зафиксирован сразу — после этого добавить билеты уже нельзя.
              </p>
            </div>
          )}
        </Card>

        <Card>
          <div className="h2" style={{ marginBottom: 10 }}>
            Честность
          </div>
          {fair ? (
            <>
              <KeyRow label="Алгоритм">{fair.algo_version}</KeyRow>
              <KeyRow label="Хеш сида" mono>
                <Hash value={fair.server_seed_hash} />
              </KeyRow>
              {fair.ticket_root && (
                <KeyRow label="Корень списка" mono>
                  <Hash value={fair.ticket_root} />
                </KeyRow>
              )}
              <KeyRow label="Билетов зафиксировано">{num(fair.ticket_count)}</KeyRow>
              {fair.external_value && <KeyRow label="Публичный сид">{fair.external_value}</KeyRow>}
              <KeyRow label="Сид раскрыт">
                {fair.revealed_at ? fmtDateTime(fair.revealed_at) : 'нет'}
              </KeyRow>
              <p className="sub" style={{ marginTop: 10, fontSize: 12 }}>
                Секретный сид не показывается даже в кабинете до раскрытия: оператор с сидом мог бы
                заранее посчитать исходы так же, как и участник.
              </p>
            </>
          ) : (
            <p className="sub">Данные о честности появятся после запуска акции.</p>
          )}
        </Card>
      </div>

      {odds.length > 0 && (
        <Card>
          <div className="h2" style={{ marginBottom: 10 }}>
            Призы: заявлено и фактически
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th>Приз</th>
                <th>Шанс</th>
                <th>Выпало</th>
                <th>Фактически</th>
              </tr>
            </thead>
            <tbody>
              {odds.map((o, i) => {
                const got = distribution?.[String(i)] ?? 0;
                return (
                  <tr key={`${o.label}-${i}`}>
                    <td>{o.label}</td>
                    <td className="mono">{percent(o.weight, oddsTotal)}%</td>
                    <td className="mono">{num(got)}</td>
                    <td className="mono faint">
                      {outcomesTotal > 0 ? `${percent(got, outcomesTotal)}%` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="sub" style={{ marginTop: 10, fontSize: 12 }}>
            Фактические доли сходятся к заявленным по мере числа попыток. Большое расхождение на
            больших числах — повод проверить таблицу весов.
          </p>
        </Card>
      )}
    </div>
  );
}

function Funnel({ rows }: { rows: Array<[string, number]> }) {
  const max = Math.max(1, ...rows.map(([, v]) => v));
  return (
    <div className="funnel">
      {rows.map(([label, value]) => (
        <div className="fr" key={label}>
          <span className="faint">{label}</span>
          <span className="bar">
            <i style={{ width: `${(value / max) * 100}%` }} />
          </span>
          <span className="n">{num(value)}</span>
        </div>
      ))}
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="stat">
      <div className="v">{value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

// ─── коды ───

function Codes({ detail, onChanged }: { detail: ConsoleCampaignDetail; onChanged: () => void }) {
  const { campaign: c, stats, batches } = detail;
  const toast = useToast();
  const { me } = useConsole();
  const [text, setText] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    setBusy(true);
    try {
      const batch = await api.importCodes(c.id, text, label || 'партия');
      toast(
        `Загружено ${batch.imported} из ${batch.total}` +
          (batch.duplicates ? `, повторов ${batch.duplicates}` : ''),
      );
      setText('');
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не удалось загрузить');
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (file: File) => {
    setText(await file.text());
  };

  return (
    <div className="stack">
      <Card>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="h2">Коды</span>
          <span className="spacer" />
          <span className="mono faint" style={{ fontSize: 12 }}>
            {num(stats.codes_used)} / {num(stats.codes_total)} использовано
          </span>
        </div>
        <Progress value={stats.codes_used} max={Math.max(1, stats.codes_total)} />
        <p className="sub" style={{ marginTop: 10 }}>
          В базе хранится только защищённый хеш каждого кода — сам код прочитать нельзя даже у нас.
          Это значит, что утечка базы не даёт возможности отыграть тираж.
        </p>
      </Card>

      {canPublish(me?.member.role) && c.status !== 'drawn' && c.status !== 'ended' && (
        <Card>
          <div className="h2" style={{ marginBottom: 10 }}>
            Загрузить партию
          </div>
          <Field label="Название партии">
            <input
              className="input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Тираж №1, типография «Нашр»"
            />
          </Field>
          <Field
            label="Коды"
            hint="CSV или по одному коду на строку. Повторы и уже загруженные коды пропускаются."
          >
            <textarea
              className="textarea mono"
              rows={8}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={'CC-0001\nCC-0002\nCC-0003'}
            />
          </Field>
          <div className="row" style={{ gap: 8 }}>
            <input
              type="file"
              accept=".csv,.txt"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onFile(file);
              }}
              style={{ fontSize: 12 }}
            />
            <span className="spacer" />
            <Button variant="pri" disabled={busy || !text.trim()} onClick={() => void upload()}>
              {busy ? 'Загружаем…' : 'Загрузить'}
            </Button>
          </div>
        </Card>
      )}

      <Card>
        <div className="h2" style={{ marginBottom: 10 }}>
          Партии
        </div>
        {batches.length === 0 ? (
          <Empty title="Партий пока нет">Без кодов участвовать нечем.</Empty>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Партия</th>
                <th>Загружено</th>
                <th>Повторы</th>
                <th>Когда</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id}>
                  <td>{b.label || '—'}</td>
                  <td className="mono">
                    {num(b.imported)} из {num(b.total)}
                  </td>
                  <td className="mono faint">{num(b.duplicates)}</td>
                  <td className="faint" style={{ fontSize: 12 }}>
                    {fmtDay(b.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <QrCard url={detail.landing_url} name={c.name} />

      <EmbedCard url={detail.landing_url} status={c.status} />

      <LinksCard campaignId={c.id} status={c.status} />
    </div>
  );
}

/**
 * Каналы: ссылки с условием, которого нет на обычной странице акции.
 *
 * Зачем они бренду. Баннер, флаер и блогер перестают быть «источником
 * трафика вообще» и становятся отдельным условием с отдельным счётчиком:
 * видно, сколько человек пришло по каждой ссылке и сколько воспользовалось.
 *
 * Зачем человеку. Обещание из баннера подтверждается на месте, с двумя
 * честными границами — сроком и числом мест. Обе видны сразу, поэтому
 * «осталось 12» здесь не приём продавца, а состояние счётчика.
 */
function LinksCard({ campaignId, status }: { campaignId: string; status: string }) {
  const toast = useToast();
  const data = useResource(() => api.campaignLinks(campaignId), [campaignId]);
  const [editing, setEditing] = useState<Partial<CampaignLink> | null>(null);

  if (status === 'draft') return null;

  const links = data.data?.links ?? [];
  const base = data.data?.base ?? '';

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast('Ссылка скопирована');
    } catch {
      toast('Браузер не дал скопировать — выдели строку руками');
    }
  };

  return (
    <Card>
      <div className="row" style={{ marginBottom: 10 }}>
        <span className="h2">Ссылки с условием</span>
        <span className="spacer" />
        <Button
          size="sm"
          icon="plus"
          onClick={() => setEditing({ bonus_points: 100, cap: 0, offer: '', label: '' })}
        >
          Новая ссылка
        </Button>
      </div>
      <p className="sub">
        Условие, которого нет на обычной странице: человек нажимает на баннер и видит надбавку с
        двумя границами — сроком и числом мест. Акцию это не меняет: механика, призы и правила
        те же, надбавка идёт сверху. Промокод, который через час лежит на форуме скидок, так не
        умеет.
      </p>

      {data.loading && <Skeleton count={2} height={56} />}

      {links.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {links.map((l) => {
            const short = `${base}/l/${l.key}`;
            return (
              <div className="linkrow" key={l.id}>
                <div style={{ minWidth: 0 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <strong style={{ fontSize: 13.5 }}>{l.label || 'Канал'}</strong>
                    {l.disabled_at ? (
                      <Badge tone="bad">выключен</Badge>
                    ) : (
                      <Badge tone="good">работает</Badge>
                    )}
                  </div>
                  <div className="n">{l.offer}</div>
                  <div className="meta">
                    <span className="mono">{short}</span>
                    {l.bonus_points > 0 && <span>+{num(l.bonus_points)} баллов</span>}
                    <span>
                      {num(l.visits)} переходов · {num(l.used)}
                      {l.cap > 0 ? ` из ${num(l.cap)}` : ''} воспользовалось
                    </span>
                    {l.window_minutes > 0 && <span>окно {num(l.window_minutes)} мин</span>}
                    {l.ends_at && <span>до {fmtDay(l.ends_at)}</span>}
                  </div>
                </div>
                <span className="spacer" />
                <div className="row" style={{ gap: 6 }}>
                  <Button size="sm" icon="copy" onClick={() => void copy(short)}>
                    Скопировать
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(l)}>
                    Править
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data.data && links.length === 0 && (
        <p className="sub" style={{ marginTop: 12 }}>
          Ссылок пока нет. Заведи одну на баннер, одну на флаер — и будет видно, что сработало.
        </p>
      )}

      {editing && (
        <LinkDialog
          campaignId={campaignId}
          link={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            data.reload();
          }}
        />
      )}
    </Card>
  );
}

function LinkDialog({
  campaignId,
  link,
  onClose,
  onDone,
}: {
  campaignId: string;
  link: Partial<CampaignLink>;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState<Partial<CampaignLink>>(link);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<CampaignLink>) => setDraft((d) => ({ ...d, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      await api.saveCampaignLink(campaignId, draft);
      toast('Ссылка сохранена');
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ovl" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <span className="h2">{draft.id ? 'Правка ссылки' : 'Новая ссылка'}</span>
        <p className="sub" style={{ margin: '6px 0 14px' }}>
          Что обещано — напиши теми же словами, что стоят на баннере: человек должен узнать
          обещание, а не догадываться о нём. Личное окно — таймер с момента перехода по ссылке:
          он идёт на сервере и не перезапускается перезагрузкой, поэтому «у тебя 15 минут» здесь
          можно говорить всерьёз.
        </p>

        <label className="field">
          <span className="lab">Что обещано</span>
          <input
            className="input"
            value={draft.offer ?? ''}
            placeholder="Двойные баллы первым 100 участникам"
            onChange={(e) => set({ offer: e.target.value })}
          />
        </label>

        <div className="fgrid">
          <label className="field">
            <span className="lab">Название канала</span>
            <input
              className="input"
              value={draft.label ?? ''}
              placeholder="Баннер в Instagram"
              onChange={(e) => set({ label: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="lab">Адрес ссылки</span>
            <input
              className="input mono"
              value={draft.key ?? ''}
              placeholder="cola-insta"
              onChange={(e) => set({ key: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="lab">Надбавка, баллов</span>
            <input
              className="input mono"
              type="number"
              min={0}
              value={draft.bonus_points ?? 0}
              onChange={(e) => set({ bonus_points: Number(e.target.value) })}
            />
          </label>
          <label className="field">
            <span className="lab">Мест — 0 без предела</span>
            <input
              className="input mono"
              type="number"
              min={0}
              value={draft.cap ?? 0}
              onChange={(e) => set({ cap: Number(e.target.value) })}
            />
          </label>
          <label className="field">
            <span className="lab">Личное окно, минут</span>
            <input
              className="input mono"
              type="number"
              min={0}
              value={draft.window_minutes ?? 0}
              onChange={(e) => set({ window_minutes: Number(e.target.value) })}
            />
          </label>
          <label className="field">
            <span className="lab">Условие до</span>
            <input
              className="input mono"
              type="date"
              value={draft.ends_at ? draft.ends_at.slice(0, 10) : ''}
              onChange={(e) =>
                set({
                  ends_at: e.target.value
                    ? new Date(`${e.target.value}T23:59:59Z`).toISOString()
                    : undefined,
                })
              }
            />
          </label>
          <label className="field">
            <span className="lab">Канал</span>
            <select
              className="select"
              value={draft.disabled_at ? '0' : '1'}
              onChange={(e) =>
                set({ disabled_at: e.target.value === '1' ? undefined : new Date().toISOString() })
              }
            >
              <option value="1">работает</option>
              <option value="0">выключен</option>
            </select>
          </label>
        </div>

        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <span className="spacer" />
          <Button variant="pri" disabled={busy || !(draft.offer ?? '').trim()} onClick={save}>
            Сохранить
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Виджет акции для сайта партнёра.
 *
 * У партнёра уже есть сайт, и акция на нём обычно живёт картинкой со словами
 * «идёт розыгрыш» — без сроков, без условий и без способа убедиться, что приз
 * существует. Виджет ставит туда ту же карточку, что и на странице акции:
 * отсчёт, приз, отметки платформы и ссылку на правила.
 *
 * Строку для вставки показываем целиком и даём скопировать: «попросите
 * разработчика» — это неделя, а здесь работы на минуту.
 */
function EmbedCard({ url, status }: { url: string; status: string }) {
  const toast = useToast();
  const [theme, setTheme] = useState<'auto' | 'light' | 'dark'>('auto');

  // Адрес акции вида https://allswipes.com/бренд/акция — виджету нужны те же
  // две части пути.
  const path = url.replace(/^https?:\/\/[^/]+\//, '');
  const origin = url.slice(0, url.length - path.length - 1);
  const snippet =
    `<script src="${origin}/embed.js" data-campaign="${path}"` +
    (theme === 'auto' ? '' : ` data-theme="${theme}"`) +
    ' async></script>';
  const iframe =
    `<iframe src="${origin}/e/${path}${theme === 'auto' ? '' : `?theme=${theme}`}" ` +
    'width="100%" height="232" style="border:0" loading="lazy" title="Акция ALLSWIPES"></iframe>';

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(`${what} скопирован`);
    } catch {
      toast('Браузер не дал скопировать — выдели строку руками');
    }
  };

  if (status === 'draft') return null;

  return (
    <Card>
      <div className="row" style={{ marginBottom: 10 }}>
        <span className="h2">Виджет на свой сайт</span>
        <span className="spacer" />
        <Segmented
          value={theme}
          options={[
            { value: 'auto', label: 'Как у посетителя' },
            { value: 'light', label: 'Светлая' },
            { value: 'dark', label: 'Тёмная' },
          ]}
          onChange={setTheme}
        />
      </div>
      <p className="sub">
        Одна строка в вёрстке сайта — и на нём появляется карточка акции с отсчётом, призом и
        ссылкой на правила. Карточка обновляется сама: менять её на сайте при продлении акции не
        нужно.
      </p>

      <div className="embed-code mono">{snippet}</div>
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <Button size="sm" icon="copy" onClick={() => void copy(snippet, 'Код виджета')}>
          Скопировать код
        </Button>
        <Button
          size="sm"
          variant="out"
          icon="external"
          onClick={() => window.open(`${origin}/e/${path}?theme=${theme}`, '_blank', 'noreferrer')}
        >
          Посмотреть карточку
        </Button>
        <span className="spacer" />
        <Button size="sm" variant="ghost" onClick={() => void copy(iframe, 'Код iframe')}>
          Вариант без скрипта
        </Button>
      </div>
      <p className="sub" style={{ marginTop: 10, fontSize: 12 }}>
        Если на сайте нельзя подключать сторонние скрипты, возьми вариант без скрипта — это
        обычный iframe, только высоту придётся задать самому.
      </p>
    </Card>
  );
}

/**
 * QR для печати на упаковке.
 *
 * Генерируется настоящей библиотекой, а не рисуется «для вида», как в
 * прототипе: этот код уходит в типографию, и декоративный квадрат вместо
 * рабочего QR означает тираж в брак.
 */
function QrCard({ url, name }: { url: string; name: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canvasRef.current || !url) return;
    QRCode.toCanvas(canvasRef.current, url, {
      width: 480,
      margin: 1,
      errorCorrectionLevel: 'M',
      // QR всегда тёмный на белом, независимо от темы: инвертированный
      // код часть сканеров не читает, а этот уходит в типографию.
      color: { dark: '#161616', light: '#ffffff' },
    }).catch(() => setError('Не удалось сгенерировать QR'));
  }, [url]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `prizma-qr-${name.replace(/\s+/g, '-').toLowerCase()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <Card>
      <div className="h2" style={{ marginBottom: 10 }}>
        QR для упаковки
      </div>
      <div className="row" style={{ gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="qrbox">
          <canvas ref={canvasRef} />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <p className="sub">Ведёт на страницу акции. Печатай на упаковке, полке или в чеке.</p>
          <div
            className="mono"
            style={{
              marginTop: 10,
              padding: '9px 12px',
              background: 'var(--surface-2)',
              border: '1px solid var(--border-2)',
              borderRadius: 9,
              fontSize: 12.5,
              overflowWrap: 'anywhere',
            }}
          >
            {url}
          </div>
          {error && (
            <p className="sub" style={{ color: 'var(--bad)', marginTop: 8 }}>
              {error}
            </p>
          )}
          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <Button onClick={download}>Скачать PNG</Button>
            <Button
              variant="ghost"
              onClick={() => {
                void navigator.clipboard?.writeText(url);
              }}
            >
              Скопировать адрес
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─── розыгрыш ───

function Draw({ detail, onChanged }: { detail: ConsoleCampaignDetail; onChanged: () => void }) {
  const { campaign: c, fair } = detail;
  const { me } = useConsole();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState('Гослото «6 из 45»');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const drawn = c.status === 'drawn';
  const ready = c.status === 'ended' && Boolean(fair?.frozen_at);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.runDraw(c.id, source, value);
      toast('Розыгрыш проведён, итоги опубликованы');
      setOpen(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось провести розыгрыш');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <Card>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="h2">Розыгрыш</span>
          <span className="spacer" />
          {drawn ? (
            <Badge tone="good">проведён</Badge>
          ) : ready ? (
            <Badge tone="warn">готов к проведению</Badge>
          ) : (
            <Badge>ждём окончания приёма кодов</Badge>
          )}
        </div>

        <KeyRow label="Дата по плану">{c.draw_at ? fmtDateTime(c.draw_at) : '—'}</KeyRow>
        <KeyRow label="Билетов">{num(c.tickets)}</KeyRow>
        <KeyRow label="Призов">{num(c.winner_count)}</KeyRow>
        {ticketChance(c.winner_count, c.tickets) !== '' && (
          <KeyRow label="Шанс билета">{ticketChance(c.winner_count, c.tickets)}%</KeyRow>
        )}
        {fair?.frozen_at && <KeyRow label="Список зафиксирован">{fmtDateTime(fair.frozen_at)}</KeyRow>}
        {fair?.ticket_root && (
          <KeyRow label="Корень списка" mono>
            <Hash value={fair.ticket_root} />
          </KeyRow>
        )}

        {!drawn && !ready && c.draw_at && (
          <p className="sub" style={{ marginTop: 12 }}>
            До розыгрыша <Countdown to={c.draw_at} />. Сначала закроется приём кодов и зафиксируется
            список билетов — только после этого можно объявлять публичный сид.
          </p>
        )}

        {ready && canPublish(me?.member.role) && (
          <div style={{ marginTop: 14 }}>
            <Button variant="pri" block onClick={() => setOpen(true)}>
              Провести розыгрыш
            </Button>
          </div>
        )}
      </Card>

      <Card>
        <div className="h2" style={{ marginBottom: 10 }}>
          Как это работает
        </div>
        <ul className="check">
          <li className="ok">
            <span className="m">1</span>
            <span>Секретный сид создан при запуске, его хеш опубликован — подменить нельзя.</span>
          </li>
          <li className="ok">
            <span className="m">2</span>
            <span>Список билетов зафиксирован корнем Меркла при закрытии приёма кодов.</span>
          </li>
          <li className="ok">
            <span className="m">3</span>
            <span>
              Ты вводишь внешний публичный сид — результат тиража, которым не управляет ни бренд,
              ни ALLSWIPES. Вводится он <b>после</b> фиксации списка, поэтому подогнать состав
              участников под сид невозможно.
            </span>
          </li>
          <li className="ok">
            <span className="m">4</span>
            <span>Победители считаются детерминированно, сид раскрывается, итоги публикуются.</span>
          </li>
        </ul>
      </Card>

      {open && (
        <Sheet title="Провести розыгрыш" onClose={() => setOpen(false)}>
          <p className="sub">
            Это необратимо. После проведения победители опубликованы, сид раскрыт, изменить результат
            нельзя — и это главное, что продаёт платформа.
          </p>

          <Field label="Источник публичного сида" hint="Чем конкретнее, тем легче участнику проверить">
            <input className="input" value={source} onChange={(e) => setSource(e.target.value)} />
          </Field>
          <Field
            label="Значение публичного сида"
            error={error ?? undefined}
            hint="Скопируй ровно то, что опубликовал источник: числа тиража, дату, номер."
          >
            <textarea
              className="textarea mono"
              rows={3}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="тираж №0498 · 18.08.2026 · 4 7 18 23 31 42"
            />
          </Field>

          <div className="row" style={{ gap: 8 }}>
            <Button variant="ghost" block onClick={() => setOpen(false)}>
              Отмена
            </Button>
            <Button variant="pri" block disabled={busy || !value.trim()} onClick={() => void run()}>
              {busy ? 'Проводим…' : 'Провести розыгрыш'}
            </Button>
          </div>
        </Sheet>
      )}
    </div>
  );
}

// ─── итоги ───

function Winners({ detail }: { detail: ConsoleCampaignDetail }) {
  const { campaign: c, winners = [], fair } = detail;

  return (
    <div className="stack">
      <Card>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="h2">Победители</span>
          <span className="spacer" />
          <span className="faint" style={{ fontSize: 12 }}>
            {num(winners.length)} {plural(winners.length, 'победитель', 'победителя', 'победителей')}
          </span>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>#</th>
              <th>Билет</th>
              <th>Приз</th>
              <th>Регион</th>
            </tr>
          </thead>
          <tbody>
            {winners.map((w) => (
              <tr key={w.position}>
                <td className="mono faint">{w.position}</td>
                <td className="mono">{w.ticket_number}</td>
                <td>{w.prize_label}</td>
                <td className="faint">{w.region || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {fair?.revealed_at && (
        <Card>
          <div className="h2" style={{ marginBottom: 10 }}>
            Доказательство
          </div>
          <KeyRow label="Публичный сид">{fair.external_value}</KeyRow>
          <KeyRow label="Источник">{fair.external_source || '—'}</KeyRow>
          <KeyRow label="Сид сервера" mono>
            <Hash value={fair.server_seed ?? ''} />
          </KeyRow>
          <KeyRow label="Корень списка" mono>
            <Hash value={fair.ticket_root ?? ''} />
          </KeyRow>
          <p className="sub" style={{ marginTop: 10 }}>
            Любой участник может пересчитать этот результат в браузере. Эта же ссылка есть на
            странице акции.
          </p>
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              window.open(`${detail.landing_url}#fair`, '_blank', 'noreferrer');
            }}
            className="btn out block"
            style={{ marginTop: 12 }}
          >
            Открыть публичное доказательство ↗
          </Link>
        </Card>
      )}

      <Card>
        <div className="h2">Выдача призов</div>
        <p className="sub" style={{ marginTop: 6 }}>
          Победителям уже отправлены уведомления. Оформленные призы появятся в разделе «Выдача
          призов».
        </p>
        <Link to="/claims" className="btn out block" style={{ marginTop: 12 }}>
          К очереди выдачи
        </Link>
        {c.status !== 'drawn' && (
          <p className="sub" style={{ marginTop: 8, color: 'var(--warn)' }}>
            Акция ещё не в статусе «розыгрыш проведён».
          </p>
        )}
      </Card>
    </div>
  );
}

/**
 * Сводка по переданным данным участников.
 *
 * Именно сводка: сколько человек передали каждое поле. Значений здесь нет и
 * не будет — бренду обещана статистика об аудитории, а не выгрузка базы
 * покупателей. Это обещание напечатано в правилах каждой акции, которая
 * что-то запрашивает.
 */
function CollectedData({ campaignId }: { campaignId: string }) {
  const data = useResource(() => api.collectedData(campaignId), [campaignId]);

  if (data.loading) return <Skeleton count={3} height={48} />;
  if (!data.data) return null;

  const max = Math.max(1, ...data.data.fields.map((f) => f.count));

  return (
    <>
      <div className="stats" style={{ marginBottom: 18 }}>
        <div className="stat">
          <div className="v">{num(data.data.total)}</div>
          <div className="l">согласий на передачу</div>
        </div>
        <div className="stat">
          <div className="v">{num(data.data.fields.length)}</div>
          <div className="l">запрошенных полей</div>
        </div>
      </div>

      <Card>
        {data.data.fields.map((field) => (
          <div className="datarow" key={field.key}>
            <div>
              <div>{field.label}</div>
              <div className="why">{field.why}</div>
            </div>
            <span className="bar">
              <i style={{ width: `${Math.round((100 * field.count) / max)}%` }} />
            </span>
            <span className="mono">{num(field.count)}</span>
          </div>
        ))}
        <p className="sub" style={{ marginTop: 12 }}>
          На экране — только счётчики: здесь ты смотришь на аудиторию, а не читаешь анкеты.
          Значения тех полей, которые человек подтвердил лично, попадают в выгрузку участников —
          ровно так, как написано в разделе 7 правил акции. Кто не подтвердил, остаётся в списке
          с пустыми колонками.
        </p>
      </Card>

      <ExportCard campaignId={campaignId} />
    </>
  );
}

/**
 * Выгрузка участников.
 *
 * Партнёр выдаёт призы и отчитывается перед собой — ему нужен список в
 * таблице, а не только счётчики. В файл попадает то, что человек передал этой
 * акции, и ничего сверх: логина и телефона там нет никогда — вход в
 * приложение остаётся делом платформы.
 */
function ExportCard({ campaignId }: { campaignId: string }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const { blob, name } = await api.exportParticipants(campaignId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      // Ссылку на файл держать незачем: браузер уже забрал его себе.
      URL.revokeObjectURL(url);
      toast('Файл выгружен');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось выгрузить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ marginTop: 18 }}>
      <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div className="h2" style={{ marginBottom: 6 }}>
            Выгрузка участников
          </div>
          <p className="sub">
            CSV для Excel и Google Sheets: участник, страна, первое участие, билеты, призы,
            состояние выдачи и подтверждённые поля анкеты. Логин и телефон в файл не попадают.
            Каждая выгрузка записывается в журнал акции.
          </p>
        </div>
        <span className="spacer" />
        <Button icon="download" disabled={busy} onClick={() => void download()}>
          Выгрузить CSV
        </Button>
      </div>
    </Card>
  );
}

/**
 * Ролик и доказательство призового фонда.
 *
 * Правится в любом статусе, в отличие от условий: условия запущенной акции —
 * обещание участникам, а фото призов и ролик — то, чем его подкрепляют.
 * Поэтому и ручка отдельная.
 */
function ProofCard({ campaign, onSaved }: { campaign: Campaign; onSaved: () => void }) {
  const toast = useToast();
  const { me } = useConsole();
  const videoUpload = me?.video_upload ?? false;
  const [video, setVideo] = useState(campaign.video_url ?? '');
  const [proof, setProof] = useState(campaign.prize_proof_url ?? '');
  const [note, setNote] = useState(campaign.prize_proof_note ?? '');
  const [busy, setBusy] = useState(false);

  const dirty =
    video !== (campaign.video_url ?? '') ||
    proof !== (campaign.prize_proof_url ?? '') ||
    note !== (campaign.prize_proof_note ?? '');

  const save = async () => {
    setBusy(true);
    try {
      await api.saveCampaignProof(campaign.id, {
        video_url: video,
        prize_proof_url: proof,
        prize_proof_note: note,
      });
      toast(
        campaign.prizes_verified_at && proof !== (campaign.prize_proof_url ?? '')
          ? 'Сохранено. Отметка платформы снята — проверят новое доказательство'
          : 'Сохранено',
      );
      onSaved();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="row" style={{ marginBottom: 10 }}>
        <span className="h2">Призы и видео</span>
        <span className="spacer" />
        {campaign.prizes_verified_at ? (
          <Badge tone="good">призы проверены</Badge>
        ) : proof ? (
          <Badge tone="warn">ждёт проверки</Badge>
        ) : null}
      </div>

      <p className="sub" style={{ marginBottom: 12 }}>
        Приложи фото призового фонда или ссылку на карточку товара — платформа посмотрит и поставит
        отметку на карточке акции. Ролик покажем прямо на странице акции
        {videoUpload ? '' : ' — вставь ссылку на видеохостинг'}. То и другое можно добавить и
        после запуска: это не условия, менять которые нельзя.
      </p>

      <Field label="Доказательство призов" hint="Фото склада, накладная или ссылка на товар">
        <MediaField
          value={proof}
          accept="image/jpeg,image/png,image/webp"
          placeholder="https://… или загрузи фото"
          onChange={setProof}
        />
      </Field>

      <Field label="Пояснение">
        <textarea
          className="input"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="12 смартфонов на складе в Душанбе, накладная от 3 октября"
        />
      </Field>

      {/* Загрузку роликов файлом платформа может выключить: файл на 50 МБ
          ложится на её диск, а каждый просмотр — это её трафик. Ссылка на
          видеохостинг работает всегда и показывается на странице так же. */}
      <Field
        label="Видео на странице акции"
        hint={
          videoUpload
            ? 'Файл до 50 МБ или ссылка на YouTube, Vimeo, Rutube или VK Видео'
            : 'Ссылка на YouTube, Vimeo, Rutube или VK Видео — ролик покажем прямо на странице'
        }
      >
        <MediaField
          value={video}
          upload={videoUpload}
          accept="video/mp4,video/webm"
          placeholder="https://youtu.be/…"
          onChange={setVideo}
        />
      </Field>

      <Button variant="pri" disabled={busy || !dirty} onClick={save}>
        Сохранить
      </Button>
    </Card>
  );
}
